import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import load from "load-script";
import { newrelic } from "@jti/fe-clients";
import { Loading, MessageBox, MessageBoxVariant, useFormContext, useTranslations } from "@jti/ui";
import { WebComponent } from "../../../components/WebComponent/WebComponent";
import { DeliveryOptionAddressField } from "../DeliveryOptionAddressField/DeliveryOptionAddressField";
import { CustomDeliveryOptionModal } from "./CustomDeliveryOptionModal";
import styles from "../DeliveryOptions.module.scss";
export const validateStoreAddress = (storeAddress) => storeAddress.storeId &&
    storeAddress.storeName &&
    storeAddress.city &&
    storeAddress.streetName &&
    storeAddress.streetNumber &&
    storeAddress.postCode;
export const CustomDeliveryOption = ({ widgetTagName = "", widgetUrl, widgetVariant = "embedded", methodCode = "", onSelectAddress, }) => {
    const { __ } = useTranslations();
    const formContext = useFormContext();
    const externalCarrierAddress = formContext.watch("externalShippingCarrier.address");
    const customDeliveryOptionRef = useRef(null);
    const formattedShippingAddress = externalCarrierAddress ? JSON.stringify(externalCarrierAddress) : "";
    const [error, setError] = useState(undefined);
    const [isWidgetLoaded, setIsWidgetLoaded] = useState(!!customElements.get(widgetTagName));
    const [isLoading, setIsLoading] = useState(false);
    const { errors } = formContext.formState;
    const loadWidget = useCallback(() => {
        if (!widgetUrl) {
            return;
        }
        setIsLoading(true);
        load(widgetUrl, { async: true }, (error) => {
            setIsLoading(false);
            if (error) {
                setError({
                    message: __("cart.checkout.delivery.custom.error.script_loading"),
                    code: "SCRIPT_LOADING_ERROR",
                });
                newrelic.noticeError(new Error("com.delivery_options.widget.error.script_loading", { cause: error }));
                console.error("Error loading custom delivery option widget", error);
                return;
            }
            setIsWidgetLoaded(true);
        });
    }, [widgetUrl]);
    const clearMagentoValidationErrors = () => {
        handleValidationErrorStatus(false);
        formContext.clearErrors();
    };
    const handleValidationErrorStatus = (hasError) => {
        if (!hasError) {
            setError(undefined);
            return;
        }
        setError({
            message: __("cart.checkout.delivery.custom.error.address_validation"),
            code: "INVALID_STORE_ADDRESS",
        });
    };
    const handleSetAddressEvent = (event) => {
        const selectedAddressData = event.detail;
        clearMagentoValidationErrors();
        handleValidationErrorStatus(!validateStoreAddress(selectedAddressData));
        const externalCarrierData = { address: selectedAddressData, methodCode };
        onSelectAddress(externalCarrierData);
    };
    const handleWidgetCrash = (event) => {
        const errorMessage = event.detail?.message || __("cart.checkout.delivery.custom.error.widget_crash");
        setError({
            message: errorMessage,
            code: "WIDGET_CRASH",
        });
    };
    const setWidgetEventListeners = useCallback(() => {
        if (!customDeliveryOptionRef.current) {
            return;
        }
        const customDeliveryOption = customDeliveryOptionRef.current;
        customDeliveryOption.addEventListener("customDeliveryOption:address:set", handleSetAddressEvent);
        customDeliveryOption.addEventListener("customDeliveryOption:widget:crash", handleWidgetCrash);
    }, [customDeliveryOptionRef.current]);
    const removeWidgetEventListeners = () => {
        if (!customDeliveryOptionRef.current) {
            return;
        }
        const customDeliveryOption = customDeliveryOptionRef.current;
        customDeliveryOption.removeEventListener("customDeliveryOption:address:set", handleSetAddressEvent);
        customDeliveryOption.removeEventListener("customDeliveryOption:widget:crash", handleWidgetCrash);
    };
    useEffect(() => {
        const isScriptInjected = document.querySelector(`script[src="${widgetUrl}"]`);
        if (isScriptInjected || isWidgetLoaded) {
            return;
        }
        loadWidget();
    }, [loadWidget, isWidgetLoaded, widgetUrl]);
    useEffect(() => {
        if (!isWidgetLoaded || !customDeliveryOptionRef.current) {
            return;
        }
        setWidgetEventListeners();
        return removeWidgetEventListeners;
    }, [isWidgetLoaded, setWidgetEventListeners, customDeliveryOptionRef]);
    useEffect(() => {
        if (errors.shippingAddress) {
            handleValidationErrorStatus(true);
        }
    }, [errors]);
    return (_jsxs("div", { className: styles.customDeliveryOptionWrapper, children: [error?.code && (_jsx(MessageBox, { className: styles.addressError, variant: MessageBoxVariant.error, "data-testid": "widget-error", isVisible: true, children: error.message })), widgetVariant === "embedded" && (_jsxs(_Fragment, { children: [isLoading && _jsx(Loading, { dataTestId: "custom-delivery-widget-loader" }), !isLoading && error?.code !== "WIDGET_CRASH" ? (_jsx(WebComponent, { ref: customDeliveryOptionRef, tagName: widgetTagName, error: error ? JSON.stringify(error) : undefined, "selected-address": formattedShippingAddress, "data-testid": "custom-delivery-option-widget-embedded" })) : null] })), widgetVariant === "modal" && (_jsxs(CustomDeliveryOptionModal, { webComponentRef: customDeliveryOptionRef, isLoading: isLoading, error: error, selectedAddress: formattedShippingAddress, children: [error?.code && error.code !== "SCRIPT_LOADING_ERROR" && (_jsx(MessageBox, { className: styles.addressError, variant: MessageBoxVariant.error, "data-testid": "widget-error", isVisible: true, children: error.message })), error?.code !== "WIDGET_CRASH" && (_jsx(WebComponent, { ref: customDeliveryOptionRef, tagName: widgetTagName, error: error ? JSON.stringify(error) : undefined, "selected-address": formattedShippingAddress, "data-testid": "custom-delivery-option-widget-modal" }))] })), externalCarrierAddress && (_jsx("div", { className: styles.selectedStore, children: _jsx(DeliveryOptionAddressField, { title: externalCarrierAddress.storeName, street: externalCarrierAddress.streetName, city: externalCarrierAddress.city, postCode: externalCarrierAddress.postCode, country: externalCarrierAddress.countryName }) }))] }));
};
//# sourceMappingURL=CustomDeliveryOption.js.map